import * as Collapsible from "@radix-ui/react-collapsible";
import * as React from "react";

import {
  collapsibleContentStyles,
  collapsibleInnerStyles,
  collapsibleRootStyles
} from "./Collapsible.css";

export type CollapsibleProps = {
  /**
   * The trigger element that will be displayed in the collapsible header
   * This should be a React element that will receive the trigger functionality
   */
  trigger: React.ReactNode;
  /**
   * The children content that will be shown/hidden when the collapsible is toggled
   */
  children: React.ReactNode;
  /**
   * Whether the collapsible is open by default
   * @default false
   */
  defaultOpen?: boolean;
  /**
   * Direction of the collapsible expansion
   * @default "down"
   */
  direction?: "up" | "down";
  /**
   * Optional callback when the collapsible is toggled
   */
  onToggle?: (isOpen: boolean) => void;
  /**
   * Whether clicking on the content should close the collapsible
   * @default false
   */
  closeOnContentClick?: boolean;
};

/**
 * A collapsible component built on top of Radix UI primitives.
 * Provides smooth animations and keyboard accessibility.
 *
 * @example
 * ```tsx
 * <CollapsibleComponent trigger={<button>Show details</button>}>
 *   <div>Some hidden content</div>
 * </CollapsibleComponent>
 * ```
 */
export const CollapsibleComponent: React.FC<CollapsibleProps> = ({
  trigger,
  children,
  defaultOpen = false,
  direction = "down",
  onToggle,
  closeOnContentClick = false
}) => {
  const [open, setOpen] = React.useState(defaultOpen);

  const handleOpenChange = React.useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen);
      onToggle?.(newOpen);
    },
    [onToggle]
  );

  const handleContentClick = React.useCallback(() => {
    if (closeOnContentClick && open) {
      handleOpenChange(false);
    }
  }, [closeOnContentClick, open, handleOpenChange]);

  return (
    <Collapsible.Root
      className={collapsibleRootStyles}
      open={open}
      onOpenChange={handleOpenChange}
    >
      <div
        data-testid="collapsible-content"
        aria-hidden={!open}
        className={collapsibleContentStyles({ direction, open })}
        onClick={handleContentClick}
        style={{
          cursor: closeOnContentClick && open ? "pointer" : "default"
        }}
      >
        <div className={collapsibleInnerStyles}>{children}</div>
      </div>

      <Collapsible.Trigger asChild>{trigger}</Collapsible.Trigger>
    </Collapsible.Root>
  );
};

export { CollapsibleComponent as Collapsible };
